import { invalidateTimerCaches } from "@/components/nav/active-timer";
import { getTimerContext } from "@/components/nav/timer-context";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import {
  SimilarWorkItemsWarning,
  useSimilarWorkItems,
} from "@/components/work-items/similar-warning";
import { ApiError, api } from "@/lib/api";
import { useDraft } from "@/lib/use-draft";
import { useRouter } from "@tanstack/react-router";
import { AlertCircle, Bug, ExternalLink, FileText, Loader2, Plus, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * In-place "New work item" modal. Port of the Next
 * `new-work-item-dialog.tsx`. Replaces the standalone
 * `/work-items/new/:type` page (still reachable by URL) for every
 * in-app "New <type>" trigger.
 */

export type WorkItemType = "feature" | "bug" | "standalone" | "hotfix";

const TYPE_META: Record<WorkItemType, { key: string; Icon: React.ElementType }> = {
  feature: { key: "feature", Icon: FileText },
  bug: { key: "bug", Icon: Bug },
  standalone: { key: "task", Icon: Wrench },
  hotfix: { key: "hotfix", Icon: AlertCircle },
};

type Project = { id: string; name: string };
type Member = { id: string; name: string | null };
type Release = { id: string; name: string };

type Props = {
  /** Trigger element (Button / Link / etc.). */
  children: React.ReactNode;
  /** Sets the work-item type — each gates its own field set. */
  type: WorkItemType;
  /** Pre-fill the project picker. */
  defaultProjectId?: string;
  /** Hide the project picker entirely. */
  lockProject?: boolean;
  /** A client's request: lands in `new` with the project's manager, so the
   *  team-side fields aren't offered. */
  asClient?: boolean;
};

// Module-level caches shared by every dialog instance.
let projectsPromise: Promise<Project[]> | null = null;
function loadProjects(): Promise<Project[]> {
  if (!projectsPromise) {
    projectsPromise = api
      .get<{ projects: Project[] }>("/api/projects")
      .then((data) => (Array.isArray(data.projects) ? data.projects : []))
      .catch(() => {
        projectsPromise = null;
        return [];
      });
  }
  return projectsPromise;
}

const membersCache = new Map<string, Promise<Member[]>>();
function loadMembers(projectId: string): Promise<Member[]> {
  let p = membersCache.get(projectId);
  if (!p) {
    p = api
      .get<Member[]>(`/api/projects/${projectId}/members`)
      .then((data) => (Array.isArray(data) ? data : []))
      .catch(() => {
        membersCache.delete(projectId);
        return [];
      });
    membersCache.set(projectId, p);
  }
  return p;
}

type ReleasesPayload = {
  useReleases: boolean;
  releases: Release[];
  defaultReleaseId: string | null;
};
const releasesCache = new Map<string, Promise<ReleasesPayload>>();
function loadReleases(projectId: string): Promise<ReleasesPayload> {
  let p = releasesCache.get(projectId);
  if (!p) {
    p = api
      .get<ReleasesPayload>(`/api/projects/${projectId}/releases`)
      .then((data) => ({
        useReleases: data?.useReleases ?? true,
        releases: Array.isArray(data?.releases) ? data.releases : [],
        defaultReleaseId: data?.defaultReleaseId ?? null,
      }))
      .catch(() => {
        releasesCache.delete(projectId);
        return { useReleases: true, releases: [], defaultReleaseId: null };
      });
    releasesCache.set(projectId, p);
  }
  return p;
}

export function NewWorkItemDialog({
  children,
  type,
  defaultProjectId,
  lockProject,
  asClient = false,
}: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const meta = TYPE_META[type];
  const typeLabel = t(`workItemLabels.type.${meta.key}`);
  // Only a feature can start in estimation/approving — a bug has no
  // estimation phase.
  const isFeature = type === "feature" && !asClient;
  const isHotfix = type === "hotfix";
  const showEstimateAndDue = !isHotfix;
  const showTeamFields = !asClient;

  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [projectUsesReleases, setProjectUsesReleases] = useState(true);
  // Feature and bug work items carry a release — hotfix and standalone
  // (task) never do.
  const showRelease =
    (type === "feature" || type === "bug") && projectUsesReleases && showTeamFields;
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);

  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  const [assigneeId, setAssigneeId] = useState("");
  const [releaseId, setReleaseId] = useState("");
  const [priority, setPriority] = useState<"low" | "normal" | "high" | "urgent">(
    isHotfix ? "urgent" : "normal",
  );
  const [initialState, setInitialState] = useState<"new" | "estimation" | "approving">("new");
  const draftScope = `new-${type}:${defaultProjectId ?? "any"}`;
  const [title, setTitle] = useDraft(`${draftScope}:title`);
  const [description, setDescription] = useDraft(`${draftScope}:description`);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState<"save" | "open" | null>(null);

  // Warn about likely-duplicate open items in the picked project as the title
  // (and description) take shape — catch the duplicate before it's created.
  const similar = useSimilarWorkItems(projectId, title, description);

  // Reset on open. Project precedence: caller's defaultProjectId,
  // then the page-level timer context, then empty.
  useEffect(() => {
    if (!open) return;
    const ctx = getTimerContext();
    setProjectId(defaultProjectId ?? ctx.projectId ?? "");
    setAssigneeId("");
    setReleaseId("");
    setPriority(isHotfix ? "urgent" : "normal");
    setInitialState("new");
    setEstimatedHours("");
    setDueDate("");
  }, [open, defaultProjectId, isHotfix]);

  useEffect(() => {
    if (!open) return;
    setLoadingProjects(projects.length === 0);
    loadProjects()
      .then(setProjects)
      .finally(() => setLoadingProjects(false));
  }, [open, projects.length]);

  useEffect(() => {
    if (!open || !projectId || !showTeamFields) {
      setMembers([]);
      return;
    }
    setLoadingMembers(true);
    loadMembers(projectId)
      .then(setMembers)
      .finally(() => setLoadingMembers(false));
  }, [open, projectId, showTeamFields]);

  useEffect(() => {
    if (!open || !projectId || isHotfix || !showTeamFields) {
      setReleases([]);
      setProjectUsesReleases(true);
      return;
    }
    setLoadingReleases(true);
    loadReleases(projectId)
      .then(({ useReleases, releases: list, defaultReleaseId }) => {
        setProjectUsesReleases(useReleases);
        setReleases(list);
        if (defaultReleaseId) setReleaseId((current) => current || defaultReleaseId);
      })
      .finally(() => setLoadingReleases(false));
  }, [open, projectId, isHotfix, showTeamFields]);

  async function submit(mode: "save" | "open") {
    if (submitting) return;
    if (!projectId) {
      toast({ title: t("workItemList.dialog.pickProjectFirst"), variant: "destructive" });
      return;
    }
    const trimmed = title.trim();
    if (!trimmed) {
      toast({ title: t("workItemList.dialog.titleRequired"), variant: "destructive" });
      return;
    }

    const payload: Record<string, unknown> = {
      type,
      projectId,
      title: trimmed,
      description: description.trim() || null,
      priority,
    };
    if (showTeamFields) payload.assigneeId = assigneeId || null;
    if (showRelease) payload.releaseId = releaseId || null;
    if (showEstimateAndDue) {
      if (estimatedHours && showTeamFields) payload.estimatedHours = Number(estimatedHours);
      if (dueDate) payload.dueDate = dueDate;
    }
    if (isFeature && initialState !== "new") {
      payload.initialStateName = initialState;
    }

    setSubmitting(mode);
    try {
      const created = await api.post<{ globalId: number }>("/api/work-items", payload);
      // Drop the cached work-items list so the timer dialog (and any
      // other caller) sees the new ticket.
      invalidateTimerCaches({ projectId });
      setTitle("");
      setDescription("");
      setOpen(false);
      if (mode === "open") {
        await router.navigate({ to: "/work-item/$id", params: { id: String(created.globalId) } });
      } else {
        toast({ title: t("workItemList.dialog.created", { type: typeLabel }), variant: "success" });
        await router.invalidate();
      }
    } catch (err) {
      toast({
        title: t("workItemList.dialog.createFailed"),
        description:
          err instanceof ApiError
            ? ((err.detail as { detail?: string; error?: string } | null)?.detail ??
              (err.detail as { error?: string } | null)?.error ??
              `HTTP ${err.status}`)
            : t("workItemList.dialog.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(null);
    }
  }

  const inputCls =
    "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* Disable backdrop-click dismiss — the form holds typed
          input that an off-target click would discard. Close
          still works via the X (top-right) and the Cancel button
          in the footer. */}
      <DialogContent
        className="max-h-[90vh] max-w-5xl overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <meta.Icon className="h-4 w-4 text-primary" />
            {t("workItemList.dialog.title", { type: typeLabel })}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit("save");
          }}
          className="space-y-3"
        >
          {!lockProject && (
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">
                {t("workItemList.dialog.project")} <span className="text-destructive">*</span>
              </span>
              <Combobox
                value={projectId}
                onChange={(v) => {
                  setProjectId(v);
                  setAssigneeId("");
                  setReleaseId("");
                  setReleases([]);
                  setProjectUsesReleases(true);
                }}
                loading={loadingProjects && projects.length === 0}
                placeholder={t("workItemList.dialog.selectProject")}
                emptyLabel={t("workItemList.dialog.noProjects")}
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
          )}

          {isFeature && (
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">{t("workItemList.dialog.initialState")}</span>
              <Select
                value={initialState}
                onChange={(v) => setInitialState(v as "new" | "estimation" | "approving")}
                className="w-full"
                options={[
                  { value: "new", label: t("workItemList.dialog.stateNew") },
                  { value: "estimation", label: t("workItemList.dialog.stateEstimation") },
                  { value: "approving", label: t("workItemList.dialog.stateApproving") },
                ]}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">{t("workItemList.dialog.priority")}</span>
              <Select
                value={priority}
                onChange={(v) => setPriority(v as "low" | "normal" | "high" | "urgent")}
                className="w-full"
                options={[
                  { value: "low", label: t("workItemLabels.priority.low") },
                  { value: "normal", label: t("workItemLabels.priority.normal") },
                  { value: "high", label: t("workItemLabels.priority.high") },
                  { value: "urgent", label: t("workItemLabels.priority.urgent") },
                ]}
              />
            </div>
            {showTeamFields && (
              <div className="block space-y-1.5">
                <span className="text-sm font-medium">{t("workItemList.dialog.assignee")}</span>
                <Combobox
                  value={assigneeId}
                  onChange={setAssigneeId}
                  disabled={!projectId || (loadingMembers && members.length === 0)}
                  loading={loadingMembers}
                  placeholder={
                    !projectId
                      ? t("workItemList.dialog.pickProject")
                      : loadingMembers
                        ? t("workItemList.dialog.loading")
                        : t("workItemList.dialog.defaultTeam")
                  }
                  emptyLabel={t("workItemList.dialog.noMembers")}
                  clearable
                  options={members.map((u) => ({ value: u.id, label: u.name ?? u.id }))}
                />
              </div>
            )}
          </div>

          {showRelease && (
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">{t("workItemList.dialog.targetRelease")}</span>
              <Combobox
                value={releaseId}
                onChange={setReleaseId}
                disabled={!projectId}
                loading={loadingReleases}
                placeholder={t("workItemList.dialog.none")}
                emptyLabel={t("workItemList.dialog.noReleases")}
                clearable
                options={releases.map((v) => ({ value: v.id, label: v.name }))}
              />
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">
              {t("workItemList.dialog.titleLabel")} <span className="text-destructive">*</span>
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={t("workItemList.dialog.titlePlaceholder", { type: typeLabel })}
              className={inputCls}
            />
          </label>

          <SimilarWorkItemsWarning matches={similar} />

          <div className="block space-y-1.5">
            <span className="text-sm font-medium">{t("workItemList.dialog.description")}</span>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              rows={16}
              autoGrow
              autoGrowMaxHeight="70vh"
              placeholder={t("workItemList.dialog.descriptionPlaceholder", {
                type: typeLabel.toLowerCase(),
              })}
              // Upload to the picked project's GitLab store; Hub
              // doesn't keep a local copy. Editor renders without
              // an upload toolbar until a project is picked.
              uploadEndpoint={projectId ? `/api/projects/${projectId}/uploads` : undefined}
            />
          </div>

          {showEstimateAndDue && (
            <div className="grid grid-cols-2 gap-3">
              {showTeamFields && (
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium">
                    {t("workItemList.dialog.estimatedHours")}
                  </span>
                  <input
                    type="number"
                    value={estimatedHours}
                    onChange={(e) => setEstimatedHours(e.target.value)}
                    step="0.25"
                    min="0"
                    placeholder="0.00"
                    className={inputCls}
                  />
                </label>
              )}
              <label htmlFor="wi-due-date" className="block space-y-1.5">
                <span className="text-sm font-medium">{t("workItemList.dialog.dueDate")}</span>
                <DatePicker id="wi-due-date" value={dueDate} onChange={setDueDate} />
              </label>
            </div>
          )}
        </form>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={submitting !== null}
          >
            {t("workItemList.dialog.cancel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void submit("open")}
            disabled={submitting !== null}
            className="gap-1.5"
          >
            {submitting === "open" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5" />
            )}
            {t("workItemList.dialog.saveAndOpen")}
          </Button>
          <Button
            size="sm"
            onClick={() => void submit("save")}
            disabled={submitting !== null}
            className="gap-1.5"
          >
            {submitting === "save" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {t("workItemList.dialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
